import Icon from '@atoms/Icon/Icon';
import Tag from '@atoms/Tag/Tag';
import Text from '@atoms/Text/Text';
import { IContactCard } from '@models/IContactCard';
import { IconName } from '@models/IIcon';
import classNames from 'classnames';
import { PropsWithChildren } from 'react';

interface IAddressItem {
  readonly check: boolean;
  readonly icon: IconName;
}

const AddressItem = ({
  check,
  icon,
  children,
}: PropsWithChildren<IAddressItem>) => {
  if (!check) {
    return null;
  }
  return (
    <div className="tw-flex tw-gap-xs">
      <Icon name={icon} className="tw-mt-1 tw-shrink-0" />
      {children}
    </div>
  );
};

const ContactCard = ({
  title,
  poBox,
  address,
  phoneNumber,
  emails,
  website,
  tags,
}: IContactCard) => {
  const phoneNumbers =
    typeof phoneNumber === 'string' ? [phoneNumber] : phoneNumber;

  return (
    <article
      className={classNames(
        'tw-flex tw-h-full tw-flex-col tw-gap-sm tw-bg-grey-10 tw-px-xs tw-py-sm',
        !tags?.length && 'lg:tw-gap-lg lg:tw-px-sm'
      )}
    >
      <Text
        variant="sub-headline"
        as="p"
        className="tw-font-light tw-text-grey-90"
      >
        {title}
      </Text>
      {!!tags?.length && (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {tags.map((tag) => (
            <span key={tag}>
              <Tag
                label={tag}
                className="tw-text-grey-70"
                as="span"
                largeText
              />
            </span>
          ))}
        </div>
      )}
      <div className="tw-mb-auto tw-flex tw-flex-col tw-gap-xs tw-text-grey-80">
        <AddressItem check={!!poBox} icon="location">
          {poBox}
        </AddressItem>
        <AddressItem check={!!address} icon="office">
          {address}
        </AddressItem>
        {!!phoneNumbers?.filter((number) => !!number)?.length && (
          <AddressItem check icon="phone">
            <div className="tw-flex tw-flex-col">
              {phoneNumbers.map((number) => (
                <a
                  href={`tel:${number}`}
                  key={number}
                  className="tw-underline tw-underline-offset-1 hover:tw-no-underline"
                >
                  {number}
                </a>
              ))}
            </div>
          </AddressItem>
        )}
        {!!emails?.filter((email) => !!email)?.length && (
          <AddressItem check icon="mail">
            <div className="tw-flex tw-flex-col">
              {emails.map((email) => (
                <a
                  className="tw-underline tw-underline-offset-1 hover:tw-no-underline"
                  key={email}
                  href={`mailto:${email}`}
                >
                  {email}
                </a>
              ))}
            </div>
          </AddressItem>
        )}
        <AddressItem check={!!website} icon="globe">
          <a
            href={website}
            className="tw-underline tw-underline-offset-1"
            target="_blank"
          >
            {website}
          </a>
        </AddressItem>
      </div>
    </article>
  );
};

export default ContactCard;
